/**
 * Every word on the marketing page, in both languages it is served in.
 *
 * The page itself is one component rendered twice (`/` and `/tr`) rather than
 * two copies of the markup: a duplicated page drifts the moment either side is
 * edited, and this page changes often. Adding a language means adding a key
 * here — no JSX is touched.
 *
 * Product nouns stay untranslated on purpose (ReplayOps, session replay,
 * tester mode, Jira): that is how the Turkish audience writes them too, and
 * inventing Turkish equivalents would read as a bad translation.
 */

import seo from './landingSeo.json';

export type Lang = 'en' | 'tr';

export interface NavLink {
  label: string;
  /** Section id on the page — the anchor is language-independent. */
  id: string;
}

export interface LandingCopy {
  /** Value for <html lang>. */
  htmlLang: string;
  /** Document title and meta description — shared with the static shells. */
  seo: { title: string; description: string };
  nav: { links: NavLink[]; docs: string; signIn: string; getStarted: string; dashboard: string };
  langSwitch: { toEn: string; toTr: string; label: string };
  hero: {
    badge: string;
    titleTop: string;
    titleAccent: string;
    subtitle: string;
    ctaPrimary: string;
    ctaDemo: string;
    ctaHow: string;
    caption: string;
  };
  outcomes: { items: { figure: string; label: string; note?: boolean }[]; footnote: string };
  logosTitle: string;
  features: { eyebrow: string; title: string; subtitle: string; items: { title: string; body: string }[] };
  steps: { eyebrow: string; title: string; items: { n: string; title: string; body: string }[] };
  privacy: {
    eyebrow: string;
    title: string;
    subtitle: string;
    beforeLabel: string;
    afterLabel: string;
    rows: { label: string; before: string; after: string }[];
    cards: { t: string; b: string }[];
    chips: string[];
    compliance: { t: string; b: string }[];
  };
  selfHosted: { overline: string; title: string; body: string; cta: string; bullets: string[] };
  why: { eyebrow: string; title: string; subtitle: string; items: { claim: string; body: string }[] };
  pricing: {
    eyebrow: string;
    title: string;
    popular: string;
    perMonth: string;
    custom: string;
    /** Shown where a price would be: prices are quoted by the team. */
    priceOnRequest: string;
    ctaCustom: string;
    ctaFree: string;
    ctaTrial: string;
    taglines: Record<string, string>;
    extras: Record<string, string[]>;
    /** Limit lines built from the live catalogue numbers. */
    limits: {
      unlimitedSessions: string;
      sessionsPerDay: (n: string) => string;
      sessionsPerMonth: (n: string) => string;
      unlimitedProjects: string;
      integrationTools: (n: number) => string;
      coverOps: string;
      coverOpsAddon: string;
      sso: string;
      projects: (n: number) => string;
      retention: (n: number) => string;
      unlimitedSeats: string;
      seats: (n: number) => string;
      core: string;
      testerSeats: (n: number) => string;
      testerUnlimited: string;
      integrations: string;
      apm: string;
      piiMasking: string;
      ingestMasking: string;
    };
  };
  faq: { eyebrow: string; title: string; items: { q: string; a: string }[] };
  demo: {
    eyebrow: string;
    title: string;
    subtitle: string;
    name: string;
    email: string;
    company: string;
    teamSize: string;
    teamSizeAny: string;
    message: string;
    submit: string;
    sending: string;
    thanks: string;
    failed: string;
    privacy: string;
  };
  finalCta: { title: string; body: string; cta: string };
  footer: { deckTitle: string; tagline: string };
}

const en: LandingCopy = {
  htmlLang: 'en',
  seo: { title: seo.en.title, description: seo.en.description },
  nav: {
    links: [
      { label: 'Features', id: 'features' },
      { label: 'Privacy', id: 'privacy' },
      { label: 'Why', id: 'why' },
      { label: 'Self-hosted', id: 'selfhosted' },
      { label: 'Pricing', id: 'pricing' },
      { label: 'FAQ', id: 'faq' },
    ],
    docs: 'Docs',
    signIn: 'Sign in',
    getStarted: 'Get started',
    dashboard: 'Dashboard',
  },
  langSwitch: { toEn: 'EN', toTr: 'TR', label: 'Language' },
  hero: {
    badge: 'Session replay with a support brain',
    titleTop: 'Turn frustrated sessions',
    titleAccent: 'into resolved tickets.',
    subtitle:
      'ReplayOps records every session, scores frustration automatically, and routes the worst ones to your team as support tickets. Run it in the cloud or entirely on your own servers.',
    ctaPrimary: 'Start free',
    ctaDemo: 'Try the live demo',
    ctaHow: 'See how it works',
    caption: 'No credit card · Self-hosted option · GDPR / KVKK ready',
  },
  outcomes: {
    items: [
      { figure: '60%', label: 'faster ticket resolution', note: true },
      { figure: '1 click', label: 'from a session to a Jira issue' },
      { figure: '~5 min', label: 'from SDK install to first replay' },
      { figure: 'zero', label: 'recordings leaving your network, self-hosted' },
    ],
    footnote:
      '* Measured with pilot customers against their previous support workflow. Results vary with team size and ticket volume.',
  },
  logosTitle: 'Connects with the tools you already use',
  features: {
    eyebrow: 'Product',
    title: 'One layer over every session',
    subtitle:
      'From the first rage click to a closed ticket. Everything support and product teams need, in one place.',
    items: [
      { title: 'Session replay + signals', body: 'Pixel-perfect rrweb replays with automatic rage-click, dead-click and error detection, distilled into a 0–100 frustration score.' },
      { title: 'Tickets from sessions', body: 'Turn a frustrated session into a support ticket in one click. The replay, signals and customer all come attached.' },
      { title: 'Error Inbox', body: 'JS, console and network errors grouped by signature across every session, ranked by impact, with jump-to-replay.' },
      { title: 'Proactive alerts', body: 'Threshold rules that fire to Slack, Teams or the in-app bell the moment frustration, rage clicks or errors spike.' },
      { title: 'Live co-browsing & chat', body: 'Watch active users move through your product in real time and message them. No browser plugin required.' },
      { title: 'Dynamic GDPR/KVKK masking', body: 'Names, e-mails, national IDs, IBANs and cards are detected in the recording and replaced before storage or egress, in Turkish and English. Scale and Enterprise.' },
      { title: 'Support & behavior analytics', body: 'Ticket volume and resolution, frustration trends, session funnels, drop-off points and click anomalies.' },
    ],
  },
  steps: {
    eyebrow: 'How it works',
    title: 'From install to insight in minutes',
    items: [
      { n: '01', title: 'Install the SDK', body: 'One script tag or npm package. Recording starts immediately, with no code changes.' },
      { n: '02', title: 'Capture & score', body: 'Every session is recorded, its errors extracted and its frustration scored automatically.' },
      { n: '03', title: 'Triage', body: 'The worst sessions surface as tickets and alerts, ranked so your team sees what matters first.' },
      { n: '04', title: 'Resolve', body: 'Watch the replay, push a bug to Jira or GitHub, and close the loop with the customer.' },
    ],
  },
  privacy: {
    eyebrow: 'Privacy',
    title: 'Dynamic GDPR & KVKK masking',
    subtitle:
      'Names, e-mails, phone numbers, national IDs, IBANs and card numbers are detected in the recording itself and replaced before they are stored or sent anywhere.',
    beforeLabel: 'In the browser',
    afterLabel: 'Stored in ReplayOps',
    rows: [
      { label: 'Page text', before: 'No results for “Ahmet Dursun”', after: 'No results for “<PERSON>”' },
      { label: 'Form value', before: 'ayse.demir@example.com', after: '<EMAIL_ADDRESS>' },
      { label: 'Support note', before: 'National ID 10000000146 · +90 532 123 45 67', after: '<TR_TCKN> · <TR_PHONE>' },
      { label: 'Request URL', before: '/api/products?q=Ahmet%20Dursun', after: '/api/products?q=<MASKED>' },
    ],
    cards: [
      { t: 'Detected, not guessed', b: 'A named-entity model reads the page the way a person would, so “Ahmet Dursun” is masked even though no rule could have predicted it. Turkish and English out of the box.' },
      { t: 'You choose what counts', b: 'Admins pick exactly which identifier types are masked, and whether an unreachable detector should block the send or let it through.' },
      { t: 'Nothing personal at rest', b: 'On Enterprise, masking runs before the recording is written. The original text exists only in transit and never lands in storage.' },
    ],
    chips: ['Scale · masking for AI & integrations', 'Enterprise · masking before storage'],
    compliance: [
      { t: 'KVKK & GDPR', b: 'Dynamic PII masking, consent gating, per-session erasure and export.' },
      { t: 'Data residency', b: 'Self-host in Türkiye or anywhere else, with recordings kept in your own storage.' },
      { t: 'Access control', b: 'Roles, per-member capabilities and an append-only audit log of every admin action.' },
      { t: 'Single sign-on', b: 'Your own identity provider over OIDC: Entra ID, Okta, Keycloak, ADFS.' },
    ],
  },
  selfHosted: {
    overline: 'Self-hosted & air-gapped',
    title: 'Run it entirely on your own infrastructure.',
    body: 'Single-tenant, offline-capable, zero third-party egress. Store recordings on S3, MinIO, Ceph or local disk; queue on SQS or Redis; search on OpenSearch or Postgres. Configure it all from the admin console.',
    cta: 'Talk to us about on-prem',
    bullets: [
      'No AWS required, runs on Postgres + Redis',
      'S3 / MinIO / Ceph / local-disk storage',
      'SSO (OIDC), RBAC & append-only audit log',
      'Data never leaves your network',
    ],
  },
  why: {
    eyebrow: 'Why ReplayOps',
    title: 'What you get that a hosted-only tool can’t give you',
    subtitle: 'Every line below is something the product does today.',
    items: [
      { claim: 'Your data never has to leave', body: 'Run the whole platform inside your own network: single-tenant, air-gapped if needed. Most session-replay tools are SaaS-only, so every recording lives on someone else’s infrastructure.' },
      { claim: 'Turkish PII, not just English', body: 'Names, TC Kimlik No, Turkish mobile numbers and IBANs are detected by a model trained on Turkish. A generic English pipeline reads straight past them.' },
      { claim: 'A replay is not the deliverable', body: 'Sessions turn into scored, deduplicated tickets and tracker issues with the replay attached, instead of leaving your team to hunt through a session list.' },
      { claim: 'Testers file bugs from inside the app', body: 'Tester mode annotates the screen and files the issue with the recording, console and network trail already attached.' },
      { claim: 'You own the deployment', body: 'Postgres and Redis are enough to run it; S3, SQS and OpenSearch are optional upgrades, not requirements.' },
    ],
  },
  pricing: {
    eyebrow: 'Pricing',
    title: 'Start free. Scale when you’re ready.',
    popular: 'Most popular',
    perMonth: '/mo',
    custom: 'Custom',
    priceOnRequest: 'Price on request',
    ctaCustom: 'Talk to us',
    ctaFree: 'Start free',
    ctaTrial: 'Start free trial',
    taglines: {
      free: 'For trying it out',
      startup: 'For a first product',
      pro: 'For growing product & support teams',
      business: 'For several products, with masking',
      enterprise: 'On-prem, SSO & a year of history',
    },
    extras: {
      pro: ['Tickets, alerts & live co-browse'],
      enterprise: ['On-prem / private cloud', 'Single-tenant / air-gapped', 'SSO & audit logs'],
    },
    limits: {
      unlimitedSessions: 'Unlimited sessions',
      sessionsPerDay: (n) => `${n} sessions / day`,
      sessionsPerMonth: (n) => `${n} sessions / month`,
      unlimitedProjects: 'Unlimited projects',
      integrationTools: (n) => `${n} integration tools`,
      coverOps: 'CoverOps integration',
      coverOpsAddon: 'CoverOps integration as an add-on',
      sso: 'Single sign-on',
      projects: (n) => `${n} ${n === 1 ? 'project' : 'projects'}`,
      retention: (n) => `${n}-day retention`,
      unlimitedSeats: 'Unlimited seats',
      seats: (n) => `${n} ${n === 1 ? 'seat' : 'seats'}`,
      core: 'Replay, signals & analytics',
      testerSeats: (n) => `Tester mode · ${n} testers`,
      testerUnlimited: 'Tester mode · unlimited testers',
      integrations: 'Integration hub',
      apm: 'APM & tracing integrations',
      piiMasking: 'Dynamic PII masking (GDPR/KVKK)',
      ingestMasking: 'Masking before storage',
    },
  },
  faq: {
    eyebrow: 'FAQ',
    title: 'The questions buyers actually ask',
    items: [
      { q: 'Where is the recorded data stored?', a: 'Wherever you point it. On the managed cloud it is our S3 bucket in the EU; self-hosted, it is your S3, MinIO, Ceph or plain disk, and nothing is sent to us. Each tenant’s recordings sit under a secret storage prefix so one tenant can never derive another’s object keys.' },
      { q: 'Are passwords and card numbers recorded?', a: 'No. Password, card-number, CVV and national-id fields are always masked in the browser before anything is transmitted, regardless of settings. Every other text input is masked by default too, and an admin can turn that off deliberately for debugging.' },
      { q: 'What about names and e-mails that appear in the page itself?', a: 'That is what dynamic masking is for. On Scale, content leaving the platform (AI prompts, tracker issues) is scrubbed; on Enterprise, masking runs before the recording is written, so the original text never reaches storage.' },
      { q: 'Can we run it on our own servers?', a: 'Yes, and it is a first-class deployment. It runs on Postgres and Redis with no AWS dependency, supports air-gapped installs, and the admin console configures storage, queue and search backends.' },
      { q: 'How much does it slow our site down?', a: 'The SDK is a single async script. Events are batched, compressed and sent in the background; recording never blocks rendering, and a session that goes idle for 60 minutes stops recording on its own.' },
      { q: 'How long are sessions kept?', a: 'By plan: 7 days on Free, up to a year on Enterprise, and configurable on self-hosted installs. Retention is enforced by a scheduled job that deletes both the metadata and the stored chunks.' },
    ],
  },
  demo: {
    eyebrow: 'Talk to us',
    title: 'See it on your own site',
    subtitle: 'Tell us a little and we will set up a walkthrough with your own data, not a canned recording.',
    name: 'Your name',
    email: 'Work email',
    company: 'Company',
    teamSize: 'Team size',
    teamSizeAny: 'Prefer not to say',
    message: 'What would you like to see?',
    submit: 'Request a demo',
    sending: 'Sending…',
    thanks: 'Thank you. We will be in touch shortly.',
    failed: 'Something went wrong. Please try again.',
    privacy: 'We use this only to get back to you.',
  },
  finalCta: {
    title: 'See what your users can’t tell you.',
    body: 'Install the SDK and watch your first frustrated session become a ticket today.',
    cta: 'Get started free',
  },
  footer: { deckTitle: 'Product Deck', tagline: 'Session replay & support intelligence' },
};

const tr: LandingCopy = {
  htmlLang: 'tr',
  seo: { title: seo.tr.title, description: seo.tr.description },
  nav: {
    links: [
      { label: 'Özellikler', id: 'features' },
      { label: 'Gizlilik', id: 'privacy' },
      { label: 'Neden', id: 'why' },
      { label: 'Kendi sunucunuzda', id: 'selfhosted' },
      { label: 'Fiyatlar', id: 'pricing' },
      { label: 'SSS', id: 'faq' },
    ],
    docs: 'Dokümanlar',
    signIn: 'Giriş yap',
    getStarted: 'Hemen başla',
    dashboard: 'Panel',
  },
  langSwitch: { toEn: 'EN', toTr: 'TR', label: 'Dil' },
  hero: {
    badge: 'Destek zekâsı olan session replay',
    titleTop: 'Sorun yaşanan oturumları',
    titleAccent: 'çözülmüş talebe dönüştürün.',
    subtitle:
      'ReplayOps her oturumu kaydeder, yaşanan zorlanmayı otomatik puanlar ve en kötülerini ekibinize destek talebi olarak iletir. Bulutta ya da tamamen kendi sunucunuzda çalışır.',
    ctaPrimary: 'Ücretsiz başla',
    ctaDemo: 'Canlı demoyu dene',
    ctaHow: 'Nasıl çalışır?',
    caption: 'Kredi kartı gerekmez · Kendi sunucunuzda kurulum · KVKK / GDPR uyumlu',
  },
  outcomes: {
    items: [
      { figure: '%60', label: 'daha hızlı talep çözümü', note: true },
      { figure: '1 tık', label: 'oturumdan Jira kaydına' },
      { figure: '~5 dk', label: 'SDK kurulumundan ilk kayda' },
      { figure: 'sıfır', label: 'kendi sunucunuzda ağdan çıkan kayıt' },
    ],
    footnote:
      '* Pilot müşterilerde, önceki destek akışlarıyla karşılaştırılarak ölçülmüştür. Sonuçlar ekip büyüklüğüne ve talep hacmine göre değişir.',
  },
  logosTitle: 'Hâlihazırda kullandığınız araçlarla çalışır',
  features: {
    eyebrow: 'Ürün',
    title: 'Her oturumun üzerinde tek bir katman',
    subtitle:
      'İlk öfke tıklamasından kapanan talebe kadar, destek ve ürün ekiplerinin ihtiyaç duyduğu her şey tek yerde.',
    items: [
      { title: 'Session replay + sinyaller', body: 'rrweb ile birebir kayıtlar; öfke tıklaması, ölü tıklama ve hata tespiti otomatik yapılır ve 0–100 arası bir zorlanma puanına dönüşür.' },
      { title: 'Oturumdan talep oluşturma', body: 'Sorunlu bir oturumu tek tıkla destek talebine çevirin. Kayıt, sinyaller ve müşteri bilgisi ekli gelir.' },
      { title: 'Hata Kutusu', body: 'JS, console ve network hataları tüm oturumlar genelinde imzasına göre gruplanır, etkisine göre sıralanır ve kayda atlanır.' },
      { title: 'Proaktif uyarılar', body: 'Zorlanma, öfke tıklaması veya hatalar yükseldiği anda Slack, Teams ya da uygulama içi zile düşen eşik kuralları.' },
      { title: 'Canlı izleme ve sohbet', body: 'Aktif kullanıcıları gerçek zamanlı izleyin ve onlara yazın. Tarayıcı eklentisi gerekmez.' },
      { title: 'Dinamik KVKK/GDPR maskeleme', body: 'İsimler, e-postalar, TC Kimlik No, IBAN ve kart numaraları kaydın içinde tespit edilip depolanmadan veya dışarı gönderilmeden önce değiştirilir. Türkçe ve İngilizce desteklenir. Scale ve Enterprise.' },
      { title: 'Destek ve davranış analitiği', body: 'Talep hacmi ve çözüm süreleri, zorlanma eğilimleri, oturum hunileri, terk noktaları ve tıklama anomalileri.' },
    ],
  },
  steps: {
    eyebrow: 'Nasıl çalışır',
    title: 'Kurulumdan içgörüye dakikalar içinde',
    items: [
      { n: '01', title: 'SDK’yı kurun', body: 'Tek bir script etiketi ya da npm paketi. Kayıt anında başlar, kod değişikliği gerekmez.' },
      { n: '02', title: 'Kaydet ve puanla', body: 'Her oturum kaydedilir, hataları çıkarılır ve zorlanma puanı otomatik hesaplanır.' },
      { n: '03', title: 'Önceliklendir', body: 'En kötü oturumlar talep ve uyarı olarak öne çıkar; ekibiniz önce gerçekten önemli olanı görür.' },
      { n: '04', title: 'Çöz', body: 'Kaydı izleyin, hatayı Jira veya GitHub’a gönderin ve müşteriye dönüş yaparak döngüyü kapatın.' },
    ],
  },
  privacy: {
    eyebrow: 'Gizlilik',
    title: 'Dinamik KVKK ve GDPR maskeleme',
    subtitle:
      'İsimler, e-postalar, telefon numaraları, TC Kimlik No, IBAN ve kart numaraları kaydın kendi içinde tespit edilir; depolanmadan ya da herhangi bir yere gönderilmeden önce değiştirilir.',
    beforeLabel: 'Tarayıcıda',
    afterLabel: 'ReplayOps’ta saklanan',
    rows: [
      { label: 'Sayfa metni', before: '“Ahmet Dursun” için 0 sonuç', after: '“<PERSON>” için 0 sonuç' },
      { label: 'Form değeri', before: 'ayse.demir@ornek.com', after: '<EMAIL_ADDRESS>' },
      { label: 'Destek notu', before: 'TCKN 10000000146 · 0532 123 45 67', after: '<TR_TCKN> · <TR_PHONE>' },
      { label: 'İstek adresi', before: '/api/urunler?q=Ahmet%20Dursun', after: '/api/urunler?q=<MASKED>' },
    ],
    cards: [
      { t: 'Tahmin değil, tespit', b: 'Varlık tanıma modeli sayfayı bir insanın okuduğu gibi okur; “Ahmet Dursun” hiçbir kuralın öngöremeyeceği hâlde maskelenir. Türkçe ve İngilizce hazır gelir.' },
      { t: 'Neyin maskeleneceğine siz karar verin', b: 'Yöneticiler hangi kimlik türlerinin maskeleneceğini ve dedektöre ulaşılamadığında gönderimin durdurulup durdurulmayacağını seçer.' },
      { t: 'Kişisel veri hiç saklanmaz', b: 'Enterprise’da maskeleme kayıt yazılmadan önce çalışır. Orijinal metin yalnızca aktarım sırasında var olur, depolamaya hiç ulaşmaz.' },
    ],
    chips: ['Scale · AI ve entegrasyonlar için maskeleme', 'Enterprise · depolamadan önce maskeleme'],
    compliance: [
      { t: 'KVKK ve GDPR', b: 'Dinamik PII maskeleme, açık rıza kontrolü, oturum bazında silme ve dışa aktarma.' },
      { t: 'Veri ikametgâhı', b: 'Türkiye’de ya da istediğiniz yerde kendi sunucunuzda çalıştırın; kayıtlar sizin depolamanızda kalır.' },
      { t: 'Erişim kontrolü', b: 'Roller, üye bazında yetkiler ve her yönetici işlemini tutan değiştirilemez denetim kaydı.' },
      { t: 'Tek oturum açma', b: 'Kendi kimlik sağlayıcınız OIDC ile: Entra ID, Okta, Keycloak, ADFS.' },
    ],
  },
  selfHosted: {
    overline: 'Kendi sunucunuzda ve internetsiz',
    title: 'Tamamen kendi altyapınızda çalıştırın.',
    body: 'Tek kiracılı, çevrimdışı çalışabilir, üçüncü tarafa sıfır veri çıkışı. Kayıtları S3, MinIO, Ceph veya yerel diskte tutun; kuyruk için SQS ya da Redis, arama için OpenSearch ya da Postgres kullanın. Hepsi yönetim panelinden ayarlanır.',
    cta: 'Kurulum için bize ulaşın',
    bullets: [
      'AWS gerekmez, Postgres + Redis yeterli',
      'S3 / MinIO / Ceph / yerel disk depolama',
      'SSO (OIDC), rol bazlı yetki ve denetim kaydı',
      'Veri ağınızın dışına çıkmaz',
    ],
  },
  why: {
    eyebrow: 'Neden ReplayOps',
    title: 'Yalnızca bulutta çalışan bir aracın veremeyeceği şeyler',
    subtitle: 'Aşağıdaki maddelerin her biri ürünün bugün yaptığı bir şey.',
    items: [
      { claim: 'Veriniz dışarı çıkmak zorunda değil', body: 'Platformun tamamını kendi ağınızda çalıştırın: tek kiracılı, gerekirse internete kapalı. Session replay araçlarının çoğu yalnızca SaaS’tır; yani her kayıt başkasının altyapısında durur.' },
      { claim: 'Sadece İngilizce değil, Türkçe PII', body: 'İsimler, TC Kimlik No, Türk cep telefonu numaraları ve IBAN’lar Türkçe eğitilmiş bir modelle tespit edilir. Genel bir İngilizce hat bu kimlik türlerini fark etmeden geçer.' },
      { claim: 'Teslim edilen şey kayıt değil', body: 'Oturumlar, kaydı ekli gelen puanlanmış ve tekilleştirilmiş taleplere ve hata kayıtlarına dönüşür; ekibiniz oturum listesinde avlanmak zorunda kalmaz.' },
      { claim: 'Testçiler hatayı uygulamanın içinden açar', body: 'Tester modu ekranı işaretler ve hatayı kayıt, console ve network izi ekli hâlde oluşturur.' },
      { claim: 'Kurulum sizin', body: 'Çalışması için Postgres ve Redis yeterli; S3, SQS ve OpenSearch zorunluluk değil, isteğe bağlı yükseltmelerdir.' },
    ],
  },
  pricing: {
    eyebrow: 'Fiyatlandırma',
    title: 'Ücretsiz başlayın. Hazır olduğunuzda büyütün.',
    popular: 'En çok tercih edilen',
    perMonth: '/ay',
    custom: 'Özel',
    priceOnRequest: 'Fiyat için bize ulaşın',
    ctaCustom: 'Bize ulaşın',
    ctaFree: 'Ücretsiz başla',
    ctaTrial: 'Ücretsiz denemeyi başlat',
    taglines: {
      free: 'Denemek isteyenler için',
      startup: 'İlk ürününüz için',
      pro: 'Büyüyen ürün ve destek ekipleri için',
      business: 'Birden çok ürün ve maskeleme için',
      enterprise: 'Kendi sunucunuzda, SSO ve bir yıllık geçmiş',
    },
    extras: {
      pro: ['Talepler, uyarılar ve canlı izleme'],
      enterprise: ['Kendi sunucunuzda / özel bulut', 'Tek kiracılı / internete kapalı', 'SSO ve denetim kayıtları'],
    },
    limits: {
      unlimitedSessions: 'Sınırsız oturum',
      sessionsPerDay: (n) => `Günde ${n} oturum`,
      sessionsPerMonth: (n) => `Ayda ${n} oturum`,
      unlimitedProjects: 'Sınırsız proje',
      integrationTools: (n) => `${n} entegrasyon aracı`,
      coverOps: 'CoverOps entegrasyonu',
      coverOpsAddon: 'CoverOps entegrasyonu (ek paket)',
      sso: 'Tek oturum açma (SSO)',
      projects: (n) => `${n} proje`,
      retention: (n) => `${n} gün saklama`,
      unlimitedSeats: 'Sınırsız kullanıcı',
      seats: (n) => `${n} kullanıcı`,
      core: 'Kayıt, sinyaller ve analitik',
      testerSeats: (n) => `Tester modu · ${n} testçi`,
      testerUnlimited: 'Tester modu · sınırsız testçi',
      integrations: 'Entegrasyon merkezi',
      apm: 'APM ve izleme entegrasyonları',
      piiMasking: 'Dinamik PII maskeleme (KVKK/GDPR)',
      ingestMasking: 'Depolamadan önce maskeleme',
    },
  },
  faq: {
    eyebrow: 'SSS',
    title: 'Alıcıların gerçekten sorduğu sorular',
    items: [
      { q: 'Kaydedilen veriler nerede saklanıyor?', a: 'Nereyi gösterirseniz orada. Yönetilen bulutta AB’deki S3 kovamızda; kendi sunucunuzda ise sizin S3, MinIO, Ceph ya da düz diskinizde ve bize hiçbir şey gönderilmez. Her müşterinin kayıtları gizli bir depolama ön ekinin altında durur; böylece bir müşteri diğerinin dosya adreslerini asla türetemez.' },
      { q: 'Şifreler ve kart numaraları kaydediliyor mu?', a: 'Hayır. Şifre, kart numarası, CVV ve kimlik numarası alanları ayardan bağımsız olarak, daha hiçbir şey iletilmeden tarayıcıda maskelenir. Diğer tüm metin alanları da varsayılan olarak maskelidir; yönetici hata ayıklama için bunu bilinçli olarak kapatabilir.' },
      { q: 'Peki sayfanın kendisinde görünen isim ve e-postalar?', a: 'Dinamik maskeleme tam olarak bunun içindir. Scale’de platformdan çıkan içerik (AI istemleri, hata kayıtları) temizlenir; Enterprise’da maskeleme kayıt yazılmadan önce çalışır, böylece orijinal metin depolamaya hiç ulaşmaz.' },
      { q: 'Kendi sunucularımızda çalıştırabilir miyiz?', a: 'Evet, hem de birinci sınıf bir kurulum biçimi olarak. Postgres ve Redis ile AWS bağımlılığı olmadan çalışır, internete kapalı kurulumları destekler; depolama, kuyruk ve arama altyapıları yönetim panelinden ayarlanır.' },
      { q: 'Sitemizi ne kadar yavaşlatır?', a: 'SDK tek bir asenkron script’tir. Olaylar toplu hâlde, sıkıştırılarak arka planda gönderilir; kayıt hiçbir zaman sayfanın çizimini bloklamaz ve 60 dakika hareketsiz kalan oturum kendiliğinden kaydı durdurur.' },
      { q: 'Oturumlar ne kadar süre saklanıyor?', a: 'Plana göre değişir: Free’de 7 gün, Enterprise’da bir yıla kadar; kendi sunucunuzdaki kurulumlarda ise ayarlanabilir. Saklama süresi, hem kaydı hem de depodaki parçaları silen zamanlanmış bir işle uygulanır.' },
    ],
  },
  demo: {
    eyebrow: 'Bize ulaşın',
    title: 'Kendi sitenizde görün',
    subtitle: 'Birkaç bilgi bırakın, hazır bir kayıt yerine kendi verinizle bir tur ayarlayalım.',
    name: 'Adınız',
    email: 'İş e-postanız',
    company: 'Şirket',
    teamSize: 'Ekip büyüklüğü',
    teamSizeAny: 'Belirtmek istemiyorum',
    message: 'Neyi görmek istersiniz?',
    submit: 'Demo talep et',
    sending: 'Gönderiliyor…',
    thanks: 'Teşekkürler. Kısa süre içinde size döneceğiz.',
    failed: 'Bir şeyler ters gitti. Lütfen tekrar deneyin.',
    privacy: 'Bu bilgileri yalnızca size dönmek için kullanıyoruz.',
  },
  finalCta: {
    title: 'Kullanıcılarınızın anlatamadığını görün.',
    body: 'SDK’yı kurun ve ilk sorunlu oturumunuzun bugün talebe dönüşmesini izleyin.',
    cta: 'Ücretsiz başlayın',
  },
  footer: { deckTitle: 'Ürün Sunumu', tagline: 'Session replay ve destek zekâsı' },
};

export const LANDING_COPY: Record<Lang, LandingCopy> = { en, tr };
